/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Target, Play, CheckCircle, XCircle, RotateCcw, Volume2, MousePointer } from 'lucide-react';
import { audioEngine, IntervalCalculator } from '../../audio/AudioEngine';
import { getIntervalColor } from '../../config/colorScheme';
import { FretPosition } from '../Fretboard';

interface EarTrainingPracticeProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
  tuning: string[];
  // Fretboard control props
  fretboardInteractive: boolean;
  setFretboardInteractive: (active: boolean) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  fretRange?: { start: number; end: number };
}

interface PracticeQuestion {
  rootNote: string;
  targetInterval: string;
  correctAnswer: string;
  fretboardPositions: Array<{ string: number; fret: number; note: string }>;
}

const EarTrainingPractice: React.FC<EarTrainingPracticeProps> = ({
  currentKey,
  currentScale,
  noteCollection,
  tuning,
  // Fretboard control props
  fretboardInteractive,
  setFretboardInteractive,
  setRootNotePosition,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  fretRange = { start: 0, end: 12 }
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<PracticeQuestion | null>(null);
  const [userAnswer, setUserAnswer] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [hintsEnabled, setHintsEnabled] = useState(true);
  const [answerMode, setAnswerMode] = useState<'buttons' | 'fretboard'>('buttons');

  // Initialize audio
  useEffect(() => {
    audioEngine.initialize().catch(console.error);
    generateNewQuestion();
  }, []);

  // Handle answer mode change
  useEffect(() => {
    setFretboardInteractive(answerMode === 'fretboard');
    
    // Clear previous answers when switching modes
    if (answerMode === 'buttons') {
      setUserFretboardAnswers([]);
      setRootNotePosition(null);
    }
  }, [answerMode, setFretboardInteractive, setUserFretboardAnswers, setRootNotePosition]);

  // Calculate available root note positions on fretboard
  const findRootNotePositions = (rootNote: string, tuning: string[], fretStart = 0, fretEnd = 12) => {
    const rootNoteName = rootNote.replace(/\d+/g, ''); // Remove octave
    const positions: FretPosition[] = [];
    
    // Safety check for tuning array
    if (!tuning || !Array.isArray(tuning)) {
      return positions;
    }
    
    
    // Check each string for the root note within visible fret range
    tuning.forEach((openNote, stringIndex) => {
      for (let fret = fretStart; fret <= fretEnd; fret++) {
        // Calculate the note at this fret position
        const noteAtFret = calculateNoteAtFret(openNote, fret);
        const noteAtFretName = noteAtFret.replace(/\d+/g, '');
        
        if (noteAtFretName === rootNoteName) {
          const position = {
            string: stringIndex + 1,
            fret: fret,
            note: rootNote
          };
          positions.push(position);
        }
      }
    });
    
    return positions;
  };

  // Calculate what note is at a specific fret on a string
  const calculateNoteAtFret = (openNote: string, fret: number): string => {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    // Handle flat notes
    const flatToSharp: { [key: string]: string } = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
    };
    
    const normalizedOpenNote = flatToSharp[openNote] || openNote;
    const openIndex = notes.indexOf(normalizedOpenNote);
    
    if (openIndex === -1) return openNote; // Fallback if note not found
    
    const targetIndex = (openIndex + fret) % 12;
    return `${notes[targetIndex]}4`; // Return with octave 4 for consistency
  };

  // Set root note position when question changes and in fretboard mode
  useEffect(() => {
    // Root note position calculation for fretboard mode
    
    if (currentQuestion && answerMode === 'fretboard' && tuning && tuning.length > 0) {
      const availablePositions = findRootNotePositions(
        currentQuestion.rootNote, 
        tuning, 
        fretRange.start, 
        fretRange.end
      );
      
      if (availablePositions.length > 0) {
        // Randomly select one of the available positions
        const randomPosition = availablePositions[Math.floor(Math.random() * availablePositions.length)];
        setRootNotePosition(randomPosition);
      } else {
        // Fallback to a default position if no positions found
        setRootNotePosition({ 
          string: 3, 
          fret: 5, 
          note: currentQuestion.rootNote 
        });
      }
    } else {
      setRootNotePosition(null);
    }
  }, [currentQuestion, answerMode, setRootNotePosition, tuning, fretRange]);

  // Practice intervals (starting with easier ones)
  const practiceIntervals = ['P5', 'P4', 'M3', 'm3', 'M2'];

  // Calculate interval between two notes
  const calculateIntervalFromNotes = (rootNote: string, targetNote: string): string => {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    // Handle flat notes
    const flatToSharp: { [key: string]: string } = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
    };
    
    const normalizedRoot = flatToSharp[rootNote] || rootNote;
    const normalizedTarget = flatToSharp[targetNote] || targetNote;
    
    const rootIndex = notes.indexOf(normalizedRoot);
    const targetIndex = notes.indexOf(normalizedTarget);
    
    if (rootIndex === -1 || targetIndex === -1) {
      return 'P1'; // Default to unison if notes not found
    }
    
    // Calculate semitone difference
    let semitones = (targetIndex - rootIndex + 12) % 12;
    
    // Map semitones to interval names
    const semitonesToInterval: { [key: number]: string } = {
      0: 'P1',   // Perfect Unison
      1: 'm2',   // Minor 2nd
      2: 'M2',   // Major 2nd
      3: 'm3',   // Minor 3rd
      4: 'M3',   // Major 3rd
      5: 'P4',   // Perfect 4th
      6: 'TRI',  // Tritone
      7: 'P5',   // Perfect 5th
      8: 'm6',   // Minor 6th
      9: 'M6',   // Major 6th
      10: 'm7',  // Minor 7th
      11: 'M7'   // Major 7th
    };
    
    return semitonesToInterval[semitones] || 'P1';
  };

  // Generate a new practice question
  const generateNewQuestion = () => {
    const rootNotes = [`${currentKey}3`, `${currentKey}4`, `${currentKey}5`];
    const randomRoot = rootNotes[Math.floor(Math.random() * rootNotes.length)];
    const randomInterval = practiceIntervals[Math.floor(Math.random() * practiceIntervals.length)];
    
    try {
      const correctAnswer = IntervalCalculator.calculateIntervalNote(randomRoot, randomInterval);
      
      // Generate some fretboard positions (simplified for now)
      const fretboardPositions = [
        { string: 1, fret: 5, note: correctAnswer },
        { string: 2, fret: 2, note: correctAnswer },
        { string: 3, fret: 7, note: correctAnswer }
      ];

      setCurrentQuestion({
        rootNote: randomRoot,
        targetInterval: randomInterval,
        correctAnswer,
        fretboardPositions
      });
      
      setUserAnswer(null);
      setShowAnswer(false);
    } catch (error) {
      console.error('Error generating question:', error);
    }
  };

  // Play the question
  const playQuestion = async () => {
    if (!currentQuestion || isPlaying) return;
    
    setIsPlaying(true);
    try {
      // Play the interval (this handles the timing internally now)
      await audioEngine.playInterval(
        currentQuestion.rootNote,
        currentQuestion.correctAnswer,
        'melodic',
        { duration: 1500 }
      );
    } catch (error) {
      console.error('Audio playback error:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  // Play just the target note (for confirmation)
  const playTargetNote = async () => {
    if (!currentQuestion) return;
    
    try {
      await audioEngine.playNote(currentQuestion.correctAnswer, { duration: 800 });
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  // Handle interval selection
  const handleIntervalGuess = (interval: string) => {
    if (!currentQuestion) return;
    
    const isCorrect = interval === currentQuestion.targetInterval;
    setUserAnswer(interval);
    setShowAnswer(true);
    
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));

    if (isCorrect) {
      // Play confirmation sound
      playTargetNote();
    }
  };

  // Next question
  const nextQuestion = () => {
    generateNewQuestion();
    setUserFretboardAnswers([]); // Clear fretboard answers for new question
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up fretboard state when component unmounts
      setFretboardInteractive(false);
      setUserFretboardAnswers([]);
      setRootNotePosition(null);
      setHighlightedFretPositions([]);
    };
  }, [setFretboardInteractive, setUserFretboardAnswers, setRootNotePosition, setHighlightedFretPositions]);

  // Reset score
  const resetScore = () => {
    setScore({ correct: 0, total: 0 });
  };

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Loading practice session...</p>
        </CardContent>
      </Card>
    );
  }

  const accuracy = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" />
            Guided Practice
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={accuracy >= 70 ? "default" : "secondary"}>
              {score.correct}/{score.total} ({accuracy}%)
            </Badge>
            <Button variant="ghost" size="sm" onClick={resetScore}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
                {/* Answer Mode Toggle */}
                <Card className="p-4">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">How would you like to answer?</h4>
            <div className="flex gap-2">
              <Button
                variant={answerMode === 'buttons' ? "default" : "outline"}
                size="sm"
                onClick={() => setAnswerMode('buttons')}
              >
                Multiple Choice
              </Button>
              <Button
                variant={answerMode === 'fretboard' ? "default" : "outline"}
                size="sm"
                onClick={() => setAnswerMode('fretboard')}
              >
                <MousePointer className="h-4 w-4 mr-1" />
                Click Fretboard
              </Button>
            </div>
          </div>
        </Card>

        {/* Instructions */}
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">
                Root note {answerMode === 'fretboard' ? 'highlighted on fretboard' : 'shown in green'}
              </span>
            </div>
            <p className="text-sm text-amber-800">
              {answerMode === 'fretboard' 
                ? 'Listen to the interval, then click the fretboard to find the second note'
                : 'Listen to the interval and identify which type you hear'
              }
            </p>
          </div>
        </Card>

        {/* Question Display */}
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">What interval do you hear?</h3>
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Root:</span>
              <span className="font-mono bg-green-100 px-3 py-1 rounded text-green-800">
                {currentQuestion.rootNote}
              </span>
              {hintsEnabled && (
                <>
                  <span className="text-sm text-muted-foreground">→ ?</span>
                </>
              )}
            </div>
          </div>

          {/* Play Button */}
          <Button 
            size="lg" 
            onClick={playQuestion} 
            disabled={isPlaying}
            className="px-8"
          >
            {isPlaying ? (
              <>
                <Volume2 className="h-5 w-5 mr-2 animate-pulse" />
                Playing...
              </>
            ) : (
              <>
                <Play className="h-5 w-5 mr-2" />
                Play Interval
              </>
            )}
          </Button>
        </div>

        {/* Answer Choices - Button Mode */}
        {!showAnswer && answerMode === 'buttons' && (
          <div className="space-y-3">
            <h4 className="font-medium text-center">Select the interval you heard:</h4>
            <div className="grid grid-cols-2 gap-2">
              {practiceIntervals.map((interval) => (
                <Button
                  key={interval}
                  variant="outline"
                  onClick={() => handleIntervalGuess(interval)}
                  className="flex items-center gap-2 p-4 h-auto"
                >
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: getIntervalColor(interval) }}
                  />
                  <div className="text-left">
                    <div className="font-semibold">{interval}</div>
                    <div className="text-xs text-muted-foreground">
                      {IntervalCalculator.getIntervalName(interval)}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Fretboard Mode Instructions */}
        {!showAnswer && answerMode === 'fretboard' && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="text-center space-y-2">
              <h4 className="font-medium text-blue-900">Click the Fretboard</h4>
              <p className="text-sm text-blue-800">
                Find the second note of the interval on the fretboard
              </p>
              {userFretboardAnswers.length > 0 && (
                <div className="text-xs text-blue-600">
                  You selected: {userFretboardAnswers[userFretboardAnswers.length - 1]?.note} 
                  (String {userFretboardAnswers[userFretboardAnswers.length - 1]?.string}, 
                  Fret {userFretboardAnswers[userFretboardAnswers.length - 1]?.fret})
                </div>
              )}
              {userFretboardAnswers.length > 0 && (
                <Button 
                  onClick={() => {
                    // Calculate the interval between root note and user's clicked note
                    const userPosition = userFretboardAnswers[userFretboardAnswers.length - 1];
                    const userNote = userPosition?.note.replace(/\d+/g, ''); // Remove octave
                    const rootNote = currentQuestion?.rootNote.replace(/\d+/g, ''); // Remove octave
                    
                    if (userNote && rootNote && currentQuestion) {
                      // Calculate what interval the user actually clicked
                      const calculatedInterval = calculateIntervalFromNotes(rootNote, userNote);
                      handleIntervalGuess(calculatedInterval);
                    }
                  }}
                  size="sm"
                  className="mt-2"
                >
                  Submit Answer
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Answer Feedback */}
        {showAnswer && (
          <Card className={`p-4 ${
            userAnswer === currentQuestion.targetInterval 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                {userAnswer === currentQuestion.targetInterval ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
                <span className={`font-semibold ${
                  userAnswer === currentQuestion.targetInterval 
                    ? 'text-green-800' 
                    : 'text-red-800'
                }`}>
                  {userAnswer === currentQuestion.targetInterval ? 'Correct!' : 'Not quite...'}
                </span>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm">
                  The correct answer was: <strong>{currentQuestion.targetInterval}</strong>
                  <span className="ml-2">
                    ({IntervalCalculator.getIntervalName(currentQuestion.targetInterval)})
                  </span>
                </p>
                {userAnswer !== currentQuestion.targetInterval && (
                  <p className="text-xs text-muted-foreground">
                    You selected: {userAnswer} ({IntervalCalculator.getIntervalName(userAnswer!)})
                  </p>
                )}
              </div>

              <Button onClick={nextQuestion} className="mt-4">
                Next Question
              </Button>
            </div>
          </Card>
        )}

        {/* Progress Encouragement */}
        {score.total > 0 && score.total % 5 === 0 && !showAnswer && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="text-center">
              <p className="text-blue-800 font-medium">
                Great progress! You've completed {score.total} questions.
              </p>
              <p className="text-sm text-blue-600 mt-1">
                {accuracy >= 80 && "Excellent accuracy! "}
                {accuracy >= 70 && accuracy < 80 && "Good work! "}
                {accuracy < 70 && "Keep practicing - you're improving! "}
              </p>
            </div>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default EarTrainingPractice;